import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllFloors,
  getAllRooms,
  getAllRoomsWithoutPagination,
  getAllTables,
  getAllTablesWithRoom,
  getBookingById,
  getBookingHistory
} from "@/api/table-floor/tableFloor";
import { remove } from "nprogress";
import { getSingleOrderApi } from "@/api/take-order/take-order";

// Thunks

export const fetchBookingHistory = createAsyncThunk(
  "liveTable/fetchBookingHistory",
  async (
    {
      restaurant_id,
      page = 1,
      per_page = 10,
      start_date,
      end_date,
      search_term,
      sort_field = "id",
      sort_order = "desc",
    },
    { rejectWithValue }
  ) => {
    const res = await getBookingHistory(restaurant_id, {
      page,
      per_page,
      start_date,
      end_date,
      search_term,
      sort_field,
      sort_order,
    });
    if (res.status === "success") return res?.data || {};
    return rejectWithValue(res.message);
  }
);

export const fetchBookingById = createAsyncThunk(
  "liveTable/fetchBookingById",
  async (booking_id, { rejectWithValue }) => {
    const res = await getBookingById(booking_id);
    if (res.status === "success") return res.data?.booking;
    return rejectWithValue(res.message);
  }
);

export const fetchOrderById = createAsyncThunk(
  "liveTable/fetchOrderById",
  async (order_id, { rejectWithValue }) => {
    const res = await getSingleOrderApi(order_id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);
export const fetchAllFloors = createAsyncThunk(
  "liveTable/fetchAllFloors",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllFloors(restaurant_id);
    if (res.status === "success") return res.data.Floors;
    return rejectWithValue(res.message);
  }
);
export const fetchAllRooms = createAsyncThunk(
  "liveTable/fetchAllRooms",
  async ({ restaurantId, floorId }, { rejectWithValue }) => {
    const res = await getAllRoomsWithoutPagination(restaurantId, floorId);

    if (res.status === "success") return res.data.rooms;
    return rejectWithValue(res.message);
  }
);

export const fetchAllTables = createAsyncThunk(
  "liveTable/fetchAllTables",
  async (room_id, { rejectWithValue }) => {
    const res = await getAllTablesWithRoom(room_id);
    if (res.status === "success") {
      localStorage.setItem("tableList", JSON.stringify(res.data.tables));
      return res.data;
    }
    return rejectWithValue(res.message);
  }
);
export const fetchAndReplaceBooking  = createAsyncThunk(
  "liveTable/fetchAndReplaceBooking",
  async (bookingId, { rejectWithValue }) => {
    const res = await getBookingById(bookingId);
    if (res.status === "success") {
      return res.data.booking;
    }
    return rejectWithValue(res.message);
  }
);


// Initial State
const initialState = {
  floors: [],
  rooms: [],
  tables: [],
  table_merges: [],
  stats: {},
  selectedFloor: null,
  selectedRoom: null,

  getLoading: false,

  success: null,
  error: null,
  requestStatus: false,

  isAddNewBookingTableModalOpen: false,
  isLiveBookingDetailsModalOpen: false,
  liveBookingDetails: null,

  bookingHistory: [],
  bookingHistoryPagination: null,
  bookingHistoryLoading: false,

  singleBooking: null,
  singleBookingLoading: false,

  singleOrder: null,
  singleOrderLoading: false,
};

// Slice
const tableSlice = createSlice({
  name: "floorsRooms",
  initialState,
  reducers: {
    clearNoticeMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
    clearBookingDetails: (state) => {
      state.singleBooking = null;
      state.singleOrder = null;
      state.singleBookingLoading = false;
      state.singleOrderLoading = false;
    },
    setSelectedFloor: (state, action) => {
      state.selectedFloor = action.payload;
    },
    setSelectedRoom: (state, action) => {
      state.selectedRoom = action.payload;
    },
    // NEW: setTables will replace the tables array and sync localStorage
    setTables: (state, action) => {
      state.tables = action.payload || [];
      localStorage.setItem("tableList", JSON.stringify(state.tables));
      state.requestStatus = true;
    },

    // NEW: Update a single table in the list (match by id or table_size.id)
    updateTableInList: (state, action) => {
      const updated = action.payload;
      state.tables = state.tables.map((t) => {
        // support matching either t.id or t.table_size?.id for flexibility
        if (t.id === updated.id || t.table_size?.id === updated.table_size?.id) {
          return { ...t, ...updated };
        }
        return t;
      });
      localStorage.setItem("tableList", JSON.stringify(state.tables));
    },

    // Optionally remove a table locally (helps after delete)
    removeTableFromList: (state, action) => {
      const idToRemove = action.payload; // could be table id or table_size.id
      state.tables = state.tables.filter(
        (t) => t.id !== idToRemove && t.table_size?.id !== idToRemove
      );
      localStorage.setItem("tableList", JSON.stringify(state.tables));
    },

    openAddNewBookingTableModal: (state) => {
      state.isAddNewBookingTableModalOpen = true;
    },
    closeAddNewBookingTableModal: (state) => {
      state.isAddNewBookingTableModalOpen = false;
    },

    updateTableStatusById: (state, action) => {
      const { table_id, status } = action.payload;

      state.tables = state.tables.map((table) =>
        table.id === table_id
          ? { ...table, status }
          : table
      );

      localStorage.setItem("tableList", JSON.stringify(state.tables));
    },
    addBookingToTable: (state, action) => {
      const booking = action.payload;
      if (!booking || !booking.table_id) return;

      state.tables = state.tables.map((table) => {
        if (Number(table.id) === Number(booking.table_id)) {
          const bookings = table.bookings || [];
          const exists = bookings.some((b) => Number(b.id) === Number(booking.id));
          const isOccupied = booking.table_status === "occupied" || booking.status === "confirmed";
          return {
            ...table,
            is_free: isOccupied ? false : table.is_free,
            is_occupied: isOccupied ? true : table.is_occupied,
            is_reserved: booking.status === "reserved" ? true : table.is_reserved,
            status: booking.table_status || (isOccupied ? "occupied" : table.status),
            bookings: exists
              ? bookings.map((b) => (Number(b.id) === Number(booking.id) ? { ...b, ...booking } : b))
              : [...bookings, booking],
          };
        }
        return table;
      });
      localStorage.setItem("tableList", JSON.stringify(state.tables));
    },
    removeBookingFromTable: (state, action) => {
      const { bookingId, tableId } = action.payload;

      state.tables = state.tables.map((table) => {
        if (Number(table.id) === Number(tableId)) {
          const remaining = (table.bookings || []).filter((booking) => Number(booking.id) !== Number(bookingId));
          const hasRemaining = remaining.length > 0;
          return {
            ...table,
            is_free: !hasRemaining ? true : table.is_free,
            is_occupied: !hasRemaining ? false : table.is_occupied,
            status: !hasRemaining ? "free" : table.status,
            bookings: remaining,
          };
        }
        return table;
      });
    },

    // updateBookingAndTableStatus: (state, action) => {
    //   const { bookingId, tableId, newBookingStatus, newTableStatus } = action.payload;
    //   state.tables = state.tables.map((table) => {
    //     if (table.id === tableId) {
    //       return {
    //         ...table,
    //         status: newTableStatus || table.status,
    //         bookings: table.bookings.map((booking) =>
    //           booking.id === bookingId
    //             ? { ...booking, status: newBookingStatus || booking.status }
    //             : booking
    //         ),
    //       };
    //     }
    //     return table;
    //   });
    // },

    updateTableStatus: (state, action) => {
      const { tableId, bookingId, newStatus } = action.payload;
      state.tables = state.tables.map((table) => {
        if (Number(table.id) === Number(tableId)) {
          const status = {
            is_free: newStatus === "free",
            is_reserved: newStatus === "reserved",
            is_occupied: newStatus === "occupied",
            is_checked_in: newStatus === "checked_in",
            status: newStatus
          };
          return { 
            ...table, 
            ...status,
            bookings: (table.bookings || []).map((booking) =>
              Number(booking.id) === Number(bookingId)
                ? { ...booking, table_status: newStatus || booking.table_status }
                : booking
            ),
          };
        }
        return table;
      });
    },

    updateTablePendingBookingCount: (state, action) => {
      const { table_id, increment = true,  } = action.payload;
      state.tables = state.tables.map((table) => {
        if (table.id === table_id) {


          const currentCount = table.pending_booking_count || 0;
          return {
            ...table,
            pending_booking_count: increment ? currentCount + 1 : currentCount - 1,
          };
        }
        return table;
      });
    },

    showLiveBookingDetailsModal: (state, action) => {
      state.isLiveBookingDetailsModalOpen = true;
      state.liveBookingDetails = action.payload;
    },

    updateBookingInTable: (state, action) => {
      const payload = action.payload;

      state.tables = state.tables.map((table) => {
        if (!table.bookings) return table;

        return {
          ...table,
          bookings: table.bookings.map((booking) => {
            if (booking.id !== payload.booking_id) return booking;

            return {
              ...booking,
              unique_id: payload.unique_id || booking.unique_id,
              reservation_date: payload.reservation_date || booking.reservation_date,
              start_time: payload.start_time || booking.start_time,
              end_time: payload.end_time || booking.end_time,
              guest: payload.guest !== undefined ? Number(payload.guest) : booking.guest,
              advance: payload.advance !== undefined ? Number(payload.advance) : booking.advance,
              status: payload.status || booking.status,
              table_status: payload.table_status || booking.table_status,
              customer: booking.customer ? {
                ...booking.customer,
                full_name: payload.customer_name || booking.customer.full_name,
              } : {
                full_name: payload.customer_name,
              }
            };
          }),
        };
      });

      localStorage.setItem("tableList", JSON.stringify(state.tables));
    },


  },
  extraReducers: (builder) => {
    builder

      // Fetch All floors
      .addCase(fetchAllFloors.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllFloors.fulfilled, (state, action) => {
        state.getLoading = false;
        state.floors = action.payload;
        state.requestStatus = true;
        if (action.payload.length > 0) {
          const activeFloor = action.payload.find(
            (floor) => floor.status === "active"
          );

          state.selectedFloor = activeFloor || action.payload[0];
        }
      })
      .addCase(fetchAllFloors.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })
      // Fetch All rooms
      .addCase(fetchAllRooms.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllRooms.fulfilled, (state, action) => {
        state.getLoading = false;
        state.rooms = action.payload;
        state.requestStatus = true;
        if (action.payload.length > 0) {
          state.selectedRoom = action.payload[0];
        }
      })
      .addCase(fetchAllRooms.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch All tables
      .addCase(fetchAllTables.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllTables.fulfilled, (state, action) => {
        state.getLoading = false;
        state.table_merges = action.payload.table_merges;
        state.stats = action.payload.stats;
        state.tables = action.payload.tables;
        state.requestStatus = true;
      })
      .addCase(fetchAllTables.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })
      .addCase(fetchAndReplaceBooking.fulfilled, (state, action) => {
        const booking = action.payload;

        state.tables = state.tables.map((table) => {
          if (table.id !== booking.table_id) return table;

          return {
            ...table,
            bookings: table.bookings.map((b) =>
              b.id === booking.id ? booking : b
            ),
          };
        });

        localStorage.setItem("tableList", JSON.stringify(state.tables));
      })

      // Fetch booking history
      .addCase(fetchBookingHistory.pending, (state) => {
        state.bookingHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchBookingHistory.fulfilled, (state, action) => {
        state.bookingHistoryLoading = false;
        state.bookingHistory = action.payload.bookings || [];
        state.bookingHistoryPagination = action.payload.pagination || null;
      })
      .addCase(fetchBookingHistory.rejected, (state, action) => {
        state.bookingHistoryLoading = false;
        state.error = action.payload;
        state.bookingHistory = [];
        state.bookingHistoryPagination = null;
      })

      // Fetch single booking
      .addCase(fetchBookingById.pending, (state) => {
        state.singleBookingLoading = true;
        state.error = null;
        state.singleBooking = null;
        state.singleOrder = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.singleBookingLoading = false;
        state.singleBooking = action.payload;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.singleBookingLoading = false;
        state.error = action.payload;
        state.singleBooking = null;
      })

      // Fetch order by id
      .addCase(fetchOrderById.pending, (state) => {
        state.singleOrderLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.singleOrderLoading = false;
        state.singleOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.singleOrderLoading = false;
        state.error = action.payload;
        state.singleOrder = null;
      });
  },
});

export const {
  clearNoticeMessages,
  clearBookingDetails,
  setSelectedFloor,
  setSelectedRoom,
  setTables,
  updateTableInList,
  removeTableFromList,
  closeAddNewBookingTableModal,
  openAddNewBookingTableModal,
  updateTableStatusById,
  updateBookingInTable,
  addBookingToTable,
  updateTablePendingBookingCount,
  removeBookingFromTable,
  // updateBookingAndTableStatus,
  updateTableStatus,
} = tableSlice.actions;
export default tableSlice.reducer;
